/**
 * FeedbackDashboard — analytics-heavy view for admins.
 *
 * Consumes `GET /api/feedback/dashboard`. Shows:
 *   • KPI row: total · new · in_progress · retesting · resolved · stalled · avg reply · median resolve
 *   • Type + priority distributions (mini bar charts)
 *   • 30-day trend (created vs resolved area chart)
 *   • Top reporters
 *
 * All charts are pure SVG (no external chart lib) to keep the bundle lean.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import i18n from "i18next";
import { FEEDBACK_TYPES, FEEDBACK_STATUS, FEEDBACK_PRIORITY } from "@/lib/feedback";
import {
    Loader2, TrendingUp, AlertTriangle, Clock, Timer, Users, RotateCw,
} from "lucide-react";

export default function FeedbackDashboard({ onDrill }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        setLoading(true);
        api.get("/feedback/dashboard")
            .then((r) => setData(r.data))
            .catch((e) => setErr(e?.response?.data?.detail || i18n.t("fbd.load_failed")))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="text-center py-16 text-[var(--jr-text-3)]" data-testid="feedback-dashboard-loading">
                <Loader2 size={20} className="animate-spin mx-auto" />
            </div>
        );
    }
    if (err) {
        return <div className="rounded-xl border border-[var(--jr-danger)]/30 bg-[var(--jr-danger)]/10 text-[var(--jr-danger)] p-4 text-sm">{err}</div>;
    }
    if (!data) return null;

    return (
        <div className="space-y-6" data-testid="feedback-dashboard">
            {/* KPI row — klik door naar de gefilterde lijst */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="fbd-kpis">
                <KpiCard label="Totaal" value={data.total} accent="#ffffff" onClick={() => onDrill?.({})} />
                <KpiCard label={FEEDBACK_STATUS.new.label}         value={data.by_status.new}         accent={FEEDBACK_STATUS.new.color}         onClick={() => onDrill?.({ status: "new" })} />
                <KpiCard label={FEEDBACK_STATUS.in_progress.label} value={data.by_status.in_progress} accent={FEEDBACK_STATUS.in_progress.color} onClick={() => onDrill?.({ status: "in_progress" })} />
                <KpiCard label={FEEDBACK_STATUS.retesting.label}   value={data.by_status.retesting}   accent={FEEDBACK_STATUS.retesting.color}
                    icon={RotateCw} sub={i18n.t("fbd.waiting_reporter")} testid="kpi-retesting" onClick={() => onDrill?.({ status: "retesting" })} />
                <KpiCard label={FEEDBACK_STATUS.resolved.label}    value={data.by_status.resolved}    accent={FEEDBACK_STATUS.resolved.color}    onClick={() => onDrill?.({ status: "resolved" })} />
                <KpiCard label={i18n.t("fbd.stalled")} value={data.stalled} accent="#ef4444"
                    icon={AlertTriangle} sub={i18n.t("fbd.stalled_sub")} testid="kpi-stalled" onClick={() => onDrill?.({ stalled: true })} />
                <KpiCard label="Avg reply"
                    value={data.reply_latency?.avg_hours != null ? `${data.reply_latency.avg_hours}h` : "—"}
                    accent="#3b82f6" icon={Clock}
                    sub={`n=${data.reply_latency?.sample_size || 0}`} />
                <KpiCard label="Median resolve"
                    value={data.resolve_time?.median_hours != null ? `${data.resolve_time.median_hours}h` : "—"}
                    accent="#22c55e" icon={Timer}
                    sub={`n=${data.resolve_time?.sample_size || 0}`} />
            </div>

            {/* Distributions — rij-klik filtert de lijst */}
            <div className="grid md:grid-cols-2 gap-4">
                <DistributionCard title="Verdeling per type" data={data.by_type} lookup={FEEDBACK_TYPES} testid="fbd-by-type"
                    onSelect={(k) => onDrill?.({ type: k })} />
                <DistributionCard title="Verdeling per prioriteit" data={data.by_priority} lookup={FEEDBACK_PRIORITY} testid="fbd-by-priority"
                    onSelect={(k) => onDrill?.({ priority: k })} />
            </div>

            {/* 30-day trend */}
            <TrendChart data={data.trend_30d || []} />

            {/* Top reporters */}
            {(data.top_reporters || []).length > 0 && (
                <div className="rounded-xl border border-white/10 bg-[var(--jr-surface)] p-4" data-testid="fbd-top-reporters">
                    <div className="flex items-center gap-2 mb-3">
                        <Users size={13} className="text-[var(--jr-text-3)]" />
                        <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">Top melders</span>
                    </div>
                    <div className="space-y-1.5">
                        {data.top_reporters.map((r, i) => (
                            <button key={r.user_id} type="button"
                                onClick={() => onDrill?.({ reporter: { id: r.user_id, name: r.user_name } })}
                                data-testid={`fbd-reporter-${r.user_id}`}
                                className="w-full flex items-center gap-3 text-sm text-left rounded-md px-1 py-0.5 hover:bg-white/[0.04] transition-colors">
                                <span className="text-[10px] font-mono w-4 text-[var(--jr-text-3)]">{i + 1}</span>
                                <span className="text-white flex-1 truncate">{r.user_name}</span>
                                <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-brand)]">{r.count} items</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---------------------------------------------------------------- */
function KpiCard({ label, value, accent, sub, icon: Icon, testid, onClick }) {
    const Tag = onClick ? "button" : "div";
    return (
        <Tag {...(onClick ? { type: "button", onClick, title: i18n.t("fbd.drill_tip") } : {})}
            className={`text-left rounded-lg border border-white/10 bg-[var(--jr-surface)] p-3 ${onClick ? "cursor-pointer hover:border-white/25 hover:bg-white/[0.03] transition-colors" : ""}`}
            data-testid={testid || `kpi-${label}`}>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] inline-flex items-center gap-1">
                {Icon && <Icon size={9} />} {label}
            </div>
            <div className="font-display text-2xl mt-0.5" style={{ color: accent }}>{value ?? "—"}</div>
            {sub && <div className="text-[10px] text-[var(--jr-text-3)] mt-0.5">{sub}</div>}
        </Tag>
    );
}

function DistributionCard({ title, data, lookup, testid, onSelect }) {
    const entries = Object.entries(data || {});
    const total = entries.reduce((sum, [, n]) => sum + (n || 0), 0);
    return (
        <div className="rounded-xl border border-white/10 bg-[var(--jr-surface)] p-4" data-testid={testid}>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)] mb-3">{title}</div>
            {entries.length === 0 || total === 0 ? (
                <div className="text-xs text-[var(--jr-text-3)] italic">{i18n.t("fbd.no_data")}</div>
            ) : (
                <div className="space-y-2">
                    {entries.map(([k, n]) => {
                        const meta = lookup[k] || {};
                        const pct = total > 0 ? Math.round((n / total) * 100) : 0;
                        return (
                            <button key={k} type="button" onClick={() => onSelect?.(k)}
                                title={i18n.t("fbd.drill_tip")} data-testid={`${testid}-row-${k}`}
                                className="w-full text-left text-xs rounded-md px-1 py-0.5 hover:bg-white/[0.04] transition-colors cursor-pointer">
                                <div className="flex items-center justify-between mb-0.5">
                                    <span className="text-white">{meta.label || k}</span>
                                    <span className="font-mono text-[var(--jr-text-3)]">{n} · {pct}%</span>
                                </div>
                                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                                    <div className="h-full rounded-full transition-all"
                                        style={{ width: `${pct}%`, background: meta.color || "#94a3b8" }} />
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* Trend chart — dual-line (created vs resolved) SVG, spans 30 days */
function TrendChart({ data }) {
    // Fill 30-day range with zeroes so gaps don't collapse the x-axis.
    const filled = fill30Days(data);
    const max = Math.max(1, ...filled.map((d) => Math.max(d.created, d.resolved)));
    const W = 720, H = 160, PAD = 24;
    const step = filled.length > 1 ? (W - PAD * 2) / (filled.length - 1) : 0;
    const pointsCreated = filled.map((d, i) => `${PAD + i * step},${H - PAD - (d.created / max) * (H - PAD * 2)}`).join(" ");
    const pointsResolved = filled.map((d, i) => `${PAD + i * step},${H - PAD - (d.resolved / max) * (H - PAD * 2)}`).join(" ");
    const totalCreated = filled.reduce((a, d) => a + d.created, 0);
    const totalResolved = filled.reduce((a, d) => a + d.resolved, 0);

    return (
        <div className="rounded-xl border border-white/10 bg-[var(--jr-surface)] p-4" data-testid="fbd-trend">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="flex items-center gap-2">
                    <TrendingUp size={13} className="text-[var(--jr-text-3)]" />
                    <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">{i18n.t("fbd.trend_30d")}</span>
                </div>
                <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.16em]">
                    <span className="inline-flex items-center gap-1 text-[var(--jr-brand)]"><Dot color="var(--jr-brand)" /> {i18n.t("myfb.st_new")} ({totalCreated})</span>
                    <span className="inline-flex items-center gap-1 text-[#22c55e]"><Dot color="#22c55e" /> Opgelost ({totalResolved})</span>
                </div>
            </div>
            <div className="overflow-x-auto">
                <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40" preserveAspectRatio="none">
                    {/* Y-axis grid */}
                    {[0, 0.25, 0.5, 0.75, 1].map((r) => (
                        <line key={r} x1={PAD} y1={H - PAD - r * (H - PAD * 2)} x2={W - PAD} y2={H - PAD - r * (H - PAD * 2)}
                            stroke="rgba(255,255,255,0.06)" strokeDasharray="2 3" />
                    ))}
                    <polyline points={pointsCreated} fill="none" stroke="#ff5a00" strokeWidth={2} strokeLinejoin="round" />
                    <polyline points={pointsResolved} fill="none" stroke="#22c55e" strokeWidth={2} strokeLinejoin="round" />
                    {/* dots */}
                    {filled.map((d, i) => (
                        <g key={i}>
                            <circle cx={PAD + i * step} cy={H - PAD - (d.created / max) * (H - PAD * 2)} r={2} fill="#ff5a00" />
                            <circle cx={PAD + i * step} cy={H - PAD - (d.resolved / max) * (H - PAD * 2)} r={2} fill="#22c55e" />
                        </g>
                    ))}
                </svg>
            </div>
            {/* X-axis labels — first + middle + last */}
            <div className="flex justify-between text-[9px] font-mono text-[var(--jr-text-3)] mt-1 px-6">
                <span>{filled[0]?.date.slice(5)}</span>
                <span>{filled[Math.floor(filled.length / 2)]?.date.slice(5)}</span>
                <span>{filled[filled.length - 1]?.date.slice(5)}</span>
            </div>
        </div>
    );
}

function Dot({ color }) {
    return <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: color }} />;
}

function fill30Days(rows) {
    const byDate = Object.fromEntries((rows || []).map((r) => [r.date, r]));
    const out = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
        const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const key = d.toISOString().slice(0, 10);
        out.push({ date: key, created: byDate[key]?.created || 0, resolved: byDate[key]?.resolved || 0 });
    }
    return out;
}
